<template>
  <rest-page ref="page" :fetch-endpoint="fetchEndpoint">
    <page-header :title="pageTitle" />
    <page-content>
      <members-preview-list-dialog
        :profile-ids="previewProfileIds"
        :date="model.create_time"
        v-if="previewProfileIds"
      />
      <key-value-table :left-width="30" :right-width="70">
        <key-value-table-row
          :label="$t('TaskHistoryList_Field_Identifier')"
          :value="$t(identifierLabel[model.identifier])"
        />

        <key-value-table-row
          v-for="(item, index) in model.paramDetails"
          :key="index"
          :label="item.label"
        >
          <template>
            <el-tooltip v-if="item.tooltip" class="item" effect="dark">
              <div slot="content" v-html="item.tooltip"></div>
              <span v-html="item.value"></span>
            </el-tooltip>

            <div v-else v-html="item.value"></div>
          </template>
        </key-value-table-row>

        <key-value-table-row
          :label="$t('TaskHistoryList_Field_CreateTime')"
          :value="createDate"
        />

        <key-value-table-row :label="$t('TaskHistoryList_Field_Status')">
          <template>
            <el-tag effect="plain" :type="taskTag.type">
              <i :class="taskTag.icon"></i>
              {{ taskTag.message }}
            </el-tag>
          </template>
        </key-value-table-row>
      </key-value-table>

      <br />

      <key-value-table
        v-if="displayResultsTable"
        :left-width="30"
        :right-width="70"
      >
        <key-value-table-row
          v-if="isSegmentPreview"
          :label="$t('TaskHistoryList_Field_Result')"
        >
          <el-link @click="showPreviewModal" icon="icon-export">
            {{ $t('TaskHistoryList_ProfilePreview') }}
          </el-link>
        </key-value-table-row>

        <key-value-table-row
          v-else
          v-for="(item, index) in model.resultDetails"
          :key="index"
          :label="item.label"
          v-show="item.label !== 'export_button'"
        >
          <div v-if="Object.hasOwn(item.value, 'link')">
            <a class="action-link" @click="processAction(item.value)">
              {{ item.value.title }}
            </a>
          </div>
          <div v-else v-html="item.value"></div>
        </key-value-table-row>
      </key-value-table>

      <template v-if="displayCampaignBreakdownTable">
        <br />

        <key-value-table
          :left-width="30"
          :right-width="70"
          v-loading="campaignBreakDownLoading"
        >
          <key-value-table-row
            :label="'<b>' + $t('TaskHistoryList_CampaignTriggerStats') + '</b>'"
          >
          </key-value-table-row>

          <key-value-table-row
            :label="$t('TaskHistoryList_Field_Targeted')"
            :value="campaignBreakdown.targeted"
          />

          <key-value-table-row
            :label="$t('TaskHistoryList_Field_Sent')"
            :value="campaignBreakdown.total_sent"
          />

          <key-value-table-row
            :label="$t('TaskHistoryList_Field_NotSent')"
            :value="campaignBreakdown.not_sent"
          />

          <key-value-table-row
            :label="
              '<i>&emsp;' +
                $t('TaskHistoryList_Field_ExcludedBySegment') +
                '</i>'
            "
            :value="campaignBreakdown.excluded_by_segment"
          />

          <key-value-table-row
            :label="
              '<i>&emsp;' + $t('TaskHistoryList_Field_ControlGroup') + '</i>'
            "
            :value="campaignBreakdown.control_group"
          />

          <key-value-table-row
            :label="
              '<i>&emsp;' +
                $t('TaskHistoryList_Field_ErrorsDuringSend') +
                '</i>'
            "
            :value="campaignBreakdown.failed_during_sending"
          />

          <template v-if="$can('UserPersonalData::Export')">
            <key-value-table-row />

            <key-value-table-row
              v-for="(item, index) in model.resultDetails"
              :key="index"
              :label="$t('TaskHistoryList_Field_DownloadSendReport')"
              v-show="item.label === 'export_button'"
            >
              <div v-if="Object.hasOwn(item.value, 'link')">
                <a class="action-link" @click="processAction(item.value)">
                  {{ item.value.title }}
                </a>
              </div>
              <div v-else v-html="item.value"></div>
            </key-value-table-row>
          </template>
        </key-value-table>
      </template>

      <br />

      <el-card class="box-card" v-if="displayErrorTable">
        <div slot="header" class="clearfix">
          <h3>{{ $t('TaskHistoryView_Errors') }}</h3>
        </div>
        <key-value-table :left-width="30" :right-width="70">
          <div v-for="(errors, index) in model.errors" :key="index">
            <value-table-row
              v-for="(error, errorIndex) in errors"
              :key="errorIndex"
              :value="error"
            />
          </div>
        </key-value-table>
      </el-card>

      <br v-if="displayWarningsTable" />

      <el-card class="box-card" v-if="displayWarningsTable">
        <div slot="header" class="clearfix">
          <h3>{{ $t('TaskHistoryView_Warnings') }}</h3>
        </div>
        <key-value-table :left-width="30" :right-width="70">
          <div v-for="(warnings, index) in model.warnings" :key="index">
            <value-table-row
              v-for="(warning, warningIndex) in warnings"
              :key="warningIndex"
              :value="warning"
            />
          </div>
        </key-value-table>
      </el-card>
    </page-content>
  </rest-page>
</template>

<script>
import RestPage from '@/common/components/rest/RestPage'
import PageHeader from '@/common/components/page/PageHeader'
import PageContent from '@/common/components/page/PageContent'
import KeyValueTable from '@/common/components/table/KeyValueTable'
import KeyValueTableRow from '@/common/components/table/KeyValueTableRow'
import ValueTableRow from '@/common/components/table/ValueTableRow'
import { projectEndpoint, projectTimezoneDate } from '@/core/helpers'
import {
  taskIdentifierLabel,
  taskStatusLabel,
  taskStatusEnum
} from '@/modules/tasks/enum/TaskRecordEnums'
import RestApi from '@/core/services/RestApi'
import MembersPreviewListDialog from '@/modules/audiences/components/MembersPreviewListDialog'
import { EventBus } from '@/core/EventBus'
import { $hasFeature } from '@/core/utils/VueAcl'

export default {
  components: {
    MembersPreviewListDialog,
    RestPage,
    PageHeader,
    PageContent,
    KeyValueTable,
    KeyValueTableRow,
    ValueTableRow
  },

  data() {
    return {
      model: {},
      identifierLabel: taskIdentifierLabel,
      statusLabel: taskStatusLabel,
      statusEnum: taskStatusEnum,
      interval: null,
      loadTask: true,
      modelLoaded: false,
      previewProfileIds: null,
      campaignBreakdown: {
        targeted: 0,
        total_sent: 0,
        not_sent: 0,
        excluded_by_segment: 0,
        control_group: 0,
        failed_during_sending: 0
      },
      campaignBreakDownLoading: true
    }
  },

  mounted() {
    EventBus.$on('members-preview-list-close', this.membersPreviewListClose)
    this.createInterval()
  },

  watch: {
    loadTask(newValue) {
      if (newValue === false) {
        clearInterval(this.interval)
      }
    },
    modelLoaded(newValue) {
      if (newValue === true) {
        this.loadCampaignBreakdown()
      }
    }
  },

  beforeDestroy() {
    clearInterval(this.interval)
  },

  computed: {
    pageTitle() {
      return (
        this.$store.getters.embeddedTitle || this.$t(this.$route.meta.title)
      )
    },

    fetchEndpoint() {
      if (this.$route.query.id) {
        return projectEndpoint({
          method: 'get',
          url: `task-histories/${this.$route.query.id}?expand=triggeredCampaignId,resultDetails,paramDetails,errors,modelWarnings`,
          handler: data => {
            if (data.modelWarnings) {
              Object.keys(data.modelWarnings).forEach(attribute => {
                this.$message({
                  message: data.modelWarnings[attribute][0],
                  type: 'warning'
                })
              })
            }
            delete data.modelWarnings
            this.model = data
            this.modelLoaded = true
          }
        })
      } else {
        return null
      }
    },

    createDate() {
      return projectTimezoneDate(this.model.create_time).format(
        'DD MMM YYYY HH:mm:ss'
      )
    },

    taskTag() {
      if (
        this.model.status === this.statusEnum.NEW ||
        this.model.status === this.statusEnum.EXECUTING
      ) {
        return {
          icon: 'el-icon-loading',
          type: 'warning',
          message: this.$t('TaskHistory_StatusLoading')
        }
      } else if (
        this.model.status === this.statusEnum.ERROR ||
        this.model.status === this.statusEnum.FATAL
      ) {
        return {
          icon: 'el-icon-error',
          type: 'danger',
          message: this.$t(this.statusLabel[this.model.status])
        }
      } else {
        return {
          icon: 'el-icon-success',
          type: 'success',
          message: this.$t(this.statusLabel[this.model.status])
        }
      }
    },

    displayErrorTable() {
      return (
        this.model.status !== this.statusEnum.NEW &&
        this.model.status !== this.statusEnum.EXECUTING &&
        this.model.errors &&
        Object.entries(this.model.errors).length > 0
      )
    },

    displayWarningsTable() {
      return (
        this.model.status !== this.statusEnum.NEW &&
        this.model.status !== this.statusEnum.EXECUTING &&
        this.model.warnings &&
        Object.entries(this.model.warnings).length > 0
      )
    },

    displayResultsTable() {
      return (
        this.model.status !== this.statusEnum.NEW &&
        this.model.status !== this.statusEnum.EXECUTING &&
        this.model.status !== this.statusEnum.ERROR &&
        this.model.status !== this.statusEnum.FATAL &&
        this.model.resultDetails &&
        this.model.resultDetails.length > 0
      )
    },

    isSegmentPreview() {
      return this.model.identifier === 'segment_preview_recipients'
    },

    displayCampaignBreakdownTable() {
      return (
        $hasFeature('featureAutomationCampaignExecution') &&
        this.modelLoaded &&
        this.model.triggeredCampaignId &&
        this.model.identifier === 'profile_import'
      )
    }
  },

  methods: {
    membersPreviewListClose() {
      this.previewProfileIds = null
    },

    showPreviewModal() {
      this.previewProfileIds = this.model.resultDetails[0].value.recipients
    },

    processAction(actionData) {
      if (Object.hasOwn(actionData, 'link')) {
        const endpoint = {
          method: 'post',
          url: actionData.link,
          data: this.prepareParams(actionData.data)
        }
        RestApi(projectEndpoint(endpoint)).then(({ data }) => {
          if (data.task_id) {
            this.$router
              .push(
                `/task/history/view?id=${data.task_id}&project_id=${this.$route.query.project_id}`
              )
              .then(_ => {
                this.$refs.page.reload()
                this.loadTask = true
                if (this.interval !== null) {
                  clearInterval(this.interval)
                }
                this.createInterval()
              })
          }
          if (data.action && data.action === 'reload') {
            this.$refs.page.reload()
            this.loadTask = true
            this.createInterval()
          }
        })
      }
    },

    prepareParams(params) {
      const result = {}

      Object.keys(params).forEach(param => {
        const parameter = params[param]
        if (parameter.startsWith('route:')) {
          const key = parameter.replaceAll('route:', '')
          result[param] = this.$route.query[key] || ''
        } else {
          result[param] = this[parameter]
        }
      })

      return result
    },

    createInterval() {
      let taskStatusEndpoint = projectEndpoint({
        method: 'get',
        url: `task-histories/${this.$route.query.id}?expand=triggeredCampaignId,resultDetails,paramDetails,errors`
      })

      this.interval = setInterval(() => {
        RestApi(taskStatusEndpoint)
          .then(({ data }) => {
            if (
              data.status !== this.statusEnum.NEW &&
              data.status !== this.statusEnum.EXECUTING
            ) {
              this.model = data
              this.modelLoaded = true
              this.loadTask = false
            }
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
      }, 2500)
    },

    loadCampaignBreakdown() {
      if (!this.displayCampaignBreakdownTable) {
        return
      }
      let campaignBreakdownEndpoint = projectEndpoint({
        method: 'get',
        url: `campaign/load-breakdown-by-task/${this.$route.query.id}`
      })

      RestApi(campaignBreakdownEndpoint)
        .then(({ data }) => {
          this.campaignBreakdown = data
          this.campaignBreakDownLoading = false
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    }
  }
}
</script>

<style scoped>
.status-loading {
  width: 16px;
  margin-right: 8px;
  margin-top: 3px;
}

>>> .el-tooltip {
  text-decoration: underline;
  cursor: default;
}

.action-link {
  cursor: pointer;
}

.campaign-breakdown-header {
  width: 100%;
  text-align: center;
  margin: 15px 0 0 0;
  color: #32404d;
  font-size: 19px;
  font-weight: normal;
  padding: 11px 22px 22px 22px;
}
</style>



// WEBPACK FOOTER //
// src/modules/tasks/pages/TaskViewPage.vue